import { useMemo } from 'react';
import type { DataProfile, ColumnProfile, ColumnType } from '../../types';

const DATE_PATTERNS = /^(date|time|month|year|quarter|day|week|period|timestamp|created|updated)/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

/** Financial OHLC column names (lowercased). */
const OHLC_COLUMNS = new Set(['open', 'high', 'low', 'close']);
const VOLUME_COLUMNS = new Set(['volume', 'vol']);
const PRICE_COLUMNS = new Set(['price', 'close', 'adj_close', 'adj close', 'last_price']);

/**
 * Classify a single column by sampling all its values.
 */
function classifyColumn(name: string, values: unknown[]): ColumnType {
  // 1. Column name matches date pattern
  if (DATE_PATTERNS.test(name)) return 'date';

  // Filter out nulls / undefined
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (nonNull.length === 0) return 'discrete';

  // 2. Check if values parse as dates (ISO strings)
  const dateCount = nonNull.filter(
    (v) => typeof v === 'string' && ISO_DATE_RE.test(v),
  ).length;
  if (dateCount > nonNull.length * 0.8) return 'date';

  // 3. Check if all values are numeric
  const numericCount = nonNull.filter((v) => {
    if (typeof v === 'number') return isFinite(v);
    if (typeof v === 'string') {
      const cleaned = v.replace(/[,$%\s]/g, '');
      return cleaned !== '' && isFinite(Number(cleaned));
    }
    return false;
  }).length;
  if (numericCount === nonNull.length) return 'numeric';

  // 4. Everything else is discrete
  return 'discrete';
}

/**
 * Compute cardinality (unique value count) for a discrete column.
 */
function computeCardinality(values: unknown[]): number {
  const unique = new Set(values.map((v) => String(v ?? '')));
  return unique.size;
}

/**
 * Build a DataProfile from a raw JSON array (ERAX `data` field).
 */
export function profileData(data: unknown): DataProfile | null {
  if (!Array.isArray(data) || data.length === 0) return null;

  const firstRow = data[0];
  if (typeof firstRow !== 'object' || firstRow === null) return null;

  const columnNames = Object.keys(firstRow);
  if (columnNames.length === 0) return null;

  const columns: ColumnProfile[] = columnNames.map((name) => {
    const values = data.map((row: Record<string, unknown>) => row[name]);
    const type = classifyColumn(name, values);
    const col: ColumnProfile = { name, type };
    if (type === 'discrete') {
      col.cardinality = computeCardinality(values);
    }
    return col;
  });

  const lowerNames = columnNames.map((n) => n.toLowerCase());
  const hasOHLC = ['open', 'high', 'low', 'close'].every((c) =>
    lowerNames.some((n) => n === c || n.includes(c)),
  );
  const hasVolume = lowerNames.some((n) => VOLUME_COLUMNS.has(n));
  const hasPrice = lowerNames.some((n) => PRICE_COLUMNS.has(n));

  return {
    columns,
    rowCount: data.length,
    numericCount: columns.filter((c) => c.type === 'numeric').length,
    discreteCount: columns.filter((c) => c.type === 'discrete').length,
    dateCount: columns.filter((c) => c.type === 'date').length,
    isFinancial: hasOHLC || (hasPrice && hasVolume),
    columnNames: lowerNames,
  };
}

/**
 * React hook that profiles the ERAX data array.
 * Returns null if data is absent or empty.
 */
export function useDataProfile(data: unknown): DataProfile | null {
  return useMemo(() => profileData(data), [data]);
}
